import { Box, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PageTitle from '../../common/PageTitle'
import { C, fadeUp, floatY, grad, shimmer } from './aboutUi'
import WhatTab from './WhatTab'
import WhyTab from './WhyTab'
import ImpactTab from './ImpactTab'
import SignalsTab from './SignalsTab'
import { fetchPaceSignals } from './signalsService'

const TABS = [
  { key: 'what', label: 'What', sub: 'What PACE is & how it decides', icon: <LightbulbOutlinedIcon />, color: C.indigo },
  { key: 'why', label: 'Why', sub: 'The problem it solves', icon: <HelpOutlineRoundedIcon />, color: C.rose },
  { key: 'impact', label: 'Impact', sub: 'What changes for your business', icon: <TrendingUpIcon />, color: C.emerald },
  { key: 'signals', label: 'Signals', sub: 'Every parameter PACE reads', icon: <TravelExploreOutlinedIcon />, color: C.violet },
]

// Hero illustration: parcels fly from the order into the PACE core, then out to the best courier.
function HeroOrbit() {
  const couriers = [
    { y: 34, c: C.sky, best: false },
    { y: 92, c: C.emerald, best: true },
    { y: 150, c: C.amber, best: false },
    { y: 208, c: C.rose, best: false },
  ]
  return (
    <Box component='svg' viewBox='0 0 420 240' sx={{ width: '100%', maxWidth: 460, height: 'auto', display: 'block' }} role='img' aria-label='An order flowing through PACE to the best courier'>
      <defs>
        <radialGradient id='core' cx='50%' cy='50%' r='50%'>
          <stop offset='0%' stopColor='#fff' stopOpacity='.95' />
          <stop offset='100%' stopColor='#fff' stopOpacity='.15' />
        </radialGradient>
      </defs>
      {/* order -> core */}
      <path id='in' d='M52 120 C110 120, 130 120, 190 120' stroke='rgba(255,255,255,.55)' strokeWidth='2' strokeDasharray='5 7' fill='none'>
        <animate attributeName='stroke-dashoffset' from='0' to='-24' dur='1s' repeatCount='indefinite' />
      </path>
      {couriers.map((k, i) => (
        <path key={i} id={`out${i}`} d={`M230 120 C290 120, 300 ${k.y}, 350 ${k.y}`}
          stroke={k.best ? '#fff' : 'rgba(255,255,255,.28)'} strokeWidth={k.best ? 3 : 1.5} strokeDasharray={k.best ? '0' : '4 6'} fill='none' />
      ))}
      {/* order box */}
      <g>
        <rect x='14' y='98' width='44' height='44' rx='10' fill='#fff' />
        <path d='M24 112 l12 -6 l12 6 v16 l-12 6 l-12 -6z M24 112 l12 6 l12 -6 M36 118 v16' stroke={C.indigo} strokeWidth='2' fill='none' strokeLinejoin='round' />
      </g>
      {/* core */}
      <circle cx='210' cy='120' r='40' fill='url(#core)'>
        <animate attributeName='r' values='36;42;36' dur='2.4s' repeatCount='indefinite' />
      </circle>
      <circle cx='210' cy='120' r='28' fill='#fff' />
      <text x='210' y='125' textAnchor='middle' fontSize='14' fontWeight='800' fill={C.indigo}>PACE</text>
      <circle cx='210' cy='120' r='52' fill='none' stroke='rgba(255,255,255,.35)' strokeDasharray='2 8' strokeWidth='2'>
        <animateTransform attributeName='transform' type='rotate' from='0 210 120' to='360 210 120' dur='14s' repeatCount='indefinite' />
      </circle>
      {/* couriers */}
      {couriers.map((k, i) => (
        <g key={i}>
          <rect x='350' y={k.y - 16} width='56' height='32' rx='9' fill={k.best ? '#fff' : 'rgba(255,255,255,.18)'} />
          <path d={`M360 ${k.y + 6} h22 v-12 h-22z M382 ${k.y - 2} h8 l6 6 v2 h-14z`} fill={k.best ? k.c : 'rgba(255,255,255,.7)'} />
          <circle cx='366' cy={k.y + 8} r='3' fill={k.best ? C.slate : 'rgba(255,255,255,.7)'} />
          <circle cx='390' cy={k.y + 8} r='3' fill={k.best ? C.slate : 'rgba(255,255,255,.7)'} />
          {k.best && (
            <g>
              <circle cx='404' cy={k.y - 14} r='9' fill={C.emerald} stroke='#fff' strokeWidth='2' />
              <path d={`M399.5 ${k.y - 14} l3 3 l5 -6`} stroke='#fff' strokeWidth='2' fill='none' strokeLinecap='round' />
            </g>
          )}
        </g>
      ))}
      {/* moving parcels */}
      <rect width='10' height='10' rx='2' fill='#FDE68A' x='-5' y='-5'>
        <animateMotion dur='3s' repeatCount='indefinite' keyPoints='0;1;1' keyTimes='0;.4;1' calcMode='linear'><mpath href='#in' /></animateMotion>
      </rect>
      <rect width='10' height='10' rx='2' fill='#FDE68A' x='-5' y='-5'>
        <animateMotion dur='3s' repeatCount='indefinite' keyPoints='0;0;1' keyTimes='0;.55;1' calcMode='linear'><mpath href='#out1' /></animateMotion>
      </rect>
    </Box>
  )
}

export default function PaceAboutPage() {
  const [params, setParams] = useSearchParams()
  const active = TABS.find((t) => t.key === params.get('tab'))?.key || 'what'
  const select = (key) => setParams({ tab: key }, { replace: true })

  return (
    <Box sx={{ pb: 8, background: C.soft, mx: { xs: -1, md: -2 }, px: { xs: 1, md: 2 }, minHeight: '100%', overflowX: 'clip' }}>
      <PageTitle>About PACE</PageTitle>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative', overflow: 'hidden', borderRadius: 5, color: '#fff', px: { xs: 3, md: 6 }, py: { xs: 4, md: 5 },
          background: 'linear-gradient(120deg, #312E81, #4F46E5, #7C3AED, #0EA5E9)', backgroundSize: '300% 300%',
          animation: `${shimmer} 16s ease infinite`, boxShadow: '0 30px 60px rgba(79,70,229,.30)',
        }}
      >
        {[{ s: 220, t: -60, l: -60 }, { s: 160, t: '60%', l: '48%' }, { s: 90, t: 20, l: '70%' }].map((b, i) => (
          <Box key={i} sx={{ position: 'absolute', width: b.s, height: b.s, top: b.t, left: b.l, borderRadius: '50%', background: 'rgba(255,255,255,.08)', animation: `${floatY} ${5 + i}s ease-in-out infinite` }} />
        ))}
        <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1.1fr .9fr' }, gap: 4, alignItems: 'center' }}>
          <Box sx={{ animation: `${fadeUp} .8s ease both` }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 99, background: 'rgba(255,255,255,.16)', fontSize: 13, fontWeight: 700 }}>
              <AutoAwesomeIcon sx={{ fontSize: 16 }} /> AI courier allocation by Prozo
            </Box>
            <Typography sx={{ fontSize: { xs: 38, md: 54 }, fontWeight: 900, lineHeight: 1.05, mt: 2, letterSpacing: -1 }}>
              PACE
            </Typography>
            <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 600, mt: 1.5, opacity: 0.95, maxWidth: 560 }}>
              For every order, pick the courier most likely to deliver it the first time, on time and at the right price.
            </Typography>
            <Typography sx={{ mt: 1.5, opacity: 0.8, maxWidth: 540 }}>
              PACE learns from millions of real shipments. It predicts how each courier will perform on each order and ranks them by your business goals.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
              {['Predicts, not guesses', 'Explains every decision', 'You set the goals', 'Zero-risk rollout'].map((t) => (
                <Box key={t} sx={{ px: 1.5, py: 0.6, borderRadius: 99, border: '1px solid rgba(255,255,255,.35)', fontSize: 13, fontWeight: 600 }}>{t}</Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}><HeroOrbit /></Box>
        </Box>
      </Box>

      {/* ── Tab switcher ─────────────────────────────────────── */}
      <Box
        role='tablist'
        sx={{
          position: 'sticky', top: 0, zIndex: 5, mt: -3.5, mx: { xs: 1, md: 4 }, p: 1, borderRadius: 4, background: 'rgba(255,255,255,.92)',
          backdropFilter: 'blur(10px)', boxShadow: '0 12px 30px rgba(15,23,42,.12)', display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1,
        }}
      >
        {TABS.map((t) => {
          const on = t.key === active
          return (
            <Box
              key={t.key}
              role='tab'
              aria-selected={on}
              tabIndex={0}
              onClick={() => select(t.key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') select(t.key) }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1, md: 1.5 }, borderRadius: 3, cursor: 'pointer', transition: 'all .25s ease',
                color: on ? '#fff' : C.ink, background: on ? grad(t.color, `${t.color}CC`) : 'transparent',
                boxShadow: on ? `0 10px 24px ${t.color}55` : 'none',
                '&:hover': { background: on ? grad(t.color, `${t.color}CC`) : `${t.color}10` },
                outline: 'none', '&:focus-visible': { boxShadow: `0 0 0 3px ${t.color}66` },
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'grid' }, placeItems: 'center', width: 40, height: 40, borderRadius: 2.5, background: on ? 'rgba(255,255,255,.2)' : `${t.color}14`, color: on ? '#fff' : t.color }}>
                {t.icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 15, md: 18 }, lineHeight: 1.1 }}>{t.label}</Typography>
                <Typography sx={{ fontSize: 12.5, opacity: on ? 0.9 : 0.65, display: { xs: 'none', md: 'block' } }}>{t.sub}</Typography>
              </Box>
            </Box>
          )
        })}
      </Box>

      <Box key={active} sx={{ mt: 5, px: { xs: 0, md: 2 }, animation: `${fadeUp} .5s ease both` }}>
        {active === 'what' && <WhatTab onNext={() => select('why')} />}
        {active === 'why' && <WhyTab onNext={() => select('impact')} />}
        {active === 'impact' && <ImpactTab />}
        {active === 'signals' && <SignalsTab load={fetchPaceSignals} merchantMode />}
      </Box>
    </Box>
  )
}
